import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Key } from "@phosphor-icons/react";
import Logo from "@/components/Logo";

// Admin-issued code redemption screen.
// Reachable at /code (linked from LoginPage and from the admin "Copy login link"
// button). Verifies the user's email + the 6-digit code the admin handed them
// without triggering a fresh OTP send. The backend route /auth/otp/verify
// already supports admin-issued codes (they live in otp_codes with
// issued_by_admin set) so no backend change is required.
export default function CodeRedeemPage() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { signIn } = useAuth();

  // If the URL provided ?code=NNNNNN, prefill it. Admins copying a deep link
  // would usually share just the path, not the code — but we support it for
  // single-click logins where the admin pastes the full URL.
  useEffect(() => {
    const c = params.get("code");
    if (c && /^\d{6}$/.test(c)) setCode(c);
  }, [params]);

  const verify = async (codeVal) => {
    const c = (codeVal ?? code).trim();
    const e = email.trim().toLowerCase();
    if (!e.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    if (c.length !== 6) {
      toast.error("Enter the 6-digit code");
      return;
    }
    try {
      setBusy(true);
      const r = await api.post("/auth/otp/verify", { email: e, code: c });
      signIn(r.data.token, r.data.user);
      toast.success("Welcome to Half Job");
      navigate("/", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Backend distinguishes between "no active code" / "expired" / "incorrect"
      // — surface each one verbatim so the user knows what to do next.
      toast.error(detail || `That code didn't work — check it and try again.`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="code-redeem-page"
      className="min-h-screen bg-kraft relative overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-25 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://static.prod-images.emergentagent.com/jobs/d521eede-4a6a-4a03-92ae-1c7d49920d93/images/bd7e8813174af2b61844c7a82c5ee0a923880e4bb2e99553afb55190a05c2908.png')",
        }}
        aria-hidden
      />
      <div className="relative z-10 min-h-screen flex flex-col px-6 py-10">
        <div className="flex items-center justify-between">
          <Logo size={24} />
        </div>

        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-md tactile-card p-6">
            <button
              onClick={() => navigate("/login")}
              data-testid="code-redeem-back"
              className="text-timber-700 flex items-center gap-1 text-sm font-semibold"
            >
              <ArrowLeft size={16} /> Back to login
            </button>

            <div className="flex items-center gap-2 mt-4">
              <Key size={20} weight="duotone" className="text-masking" />
              <p className="label-cap">Admin sign-in code</p>
            </div>
            <h1
              data-testid="code-redeem-title"
              className="font-heading text-3xl font-bold text-timber-900 mt-1 leading-tight"
            >
              {`Have a code from a Half Job admin?`}
            </h1>
            <p className="text-timber-700 mt-2 text-sm">
              {`Enter the email the admin used and the 6-digit code they shared. We won't send a new code — yours is already active.`}
            </p>

            <div className="mt-6 space-y-3">
              <div>
                <label className="label-cap">Email</label>
                <Input
                  data-testid="code-redeem-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@home.com"
                  className="mt-1 min-h-[56px] bg-chalk border-2 border-stone_border rounded-xl text-lg"
                />
              </div>

              <div>
                <label className="label-cap">Code</label>
                <div className="mt-1 flex justify-center" data-testid="code-redeem-otp-wrapper">
                  <InputOTP
                    maxLength={6}
                    value={code}
                    onChange={(v) => {
                      setCode(v);
                      if (v.length === 6 && email.trim().includes("@")) verify(v);
                    }}
                  >
                    <InputOTPGroup>
                      {[0,1,2,3,4,5].map(i => (
                        <InputOTPSlot
                          key={i}
                          index={i}
                          data-testid={`code-redeem-slot-${i}`}
                          className="w-12 h-14 text-2xl font-bold border-2 border-stone_border bg-chalk"
                        />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>

              <Button
                data-testid="code-redeem-submit-btn"
                onClick={() => verify()}
                disabled={busy || code.length !== 6 || !email.trim().includes("@")}
                className="w-full bg-masking text-timber-900 hover:bg-masking-600 hover:text-white min-h-[56px] text-base font-bold btn-press"
              >
                {busy ? "Signing in…" : "Continue"}
              </Button>
            </div>

            <p className="text-xs text-timber-500 mt-6 leading-relaxed">
              Codes expire after a short window and can only be used once. If yours
              doesn&apos;t work, ask your admin to issue a fresh one.
            </p>

            <p className="text-xs text-timber-500 mt-4 leading-relaxed">
              Don&apos;t have a code?{" "}
              <button
                onClick={() => navigate("/login")}
                data-testid="code-redeem-back-to-login"
                className="text-masking font-bold underline"
              >
                Use the normal sign-in
              </button>
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-timber-500 mt-6">
          A DIY copilot that gets the job done · v1
        </p>
        <p className="text-center text-[11px] text-timber-500 mt-2">
          <a href="/terms" className="underline hover:text-timber-700">
            Terms &amp; Acceptable Use
          </a>
        </p>
      </div>
    </div>
  );
}
